import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Rss, Search, Activity, AlertCircle, CheckCircle,
  ExternalLink, Loader2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Status

function HealthDot({ status, latestFetch }) {
  if (!latestFetch) {
    return <span className="w-2 h-2 rounded-full bg-slate-300" title="Never fetched" />;
  }
  const colour = {
    healthy:  'bg-emerald-500',
    warning:  'bg-amber-500',
    degraded: 'bg-orange-500',
    failed:   'bg-red-500',
    blocked:  'bg-rose-600',
    unknown:  'bg-slate-400',
  }[status] || 'bg-slate-400';
  return <span className={`w-2 h-2 rounded-full ${colour}`} title={status} />;
}


function SummaryCards({ summary }) {
  const items = [
    { label: 'Total Publications',   value: summary?.total_publications ?? 0,                icon: Rss },
    { label: 'No Subscriptions',     value: summary?.publications_without_subscriptions ?? 0, icon: AlertCircle, accent: 'amber' },
    { label: 'Active Sources',       value: summary?.active_sources ?? 0,                    icon: CheckCircle },
    { label: 'Active Subscriptions', value: summary?.active_subscriptions ?? 0,              icon: Activity },
    { label: 'Stories Published',    value: summary?.total_stories_published ?? 0,           icon: ExternalLink },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      {items.map(({ label, value, icon: Icon, accent }) => (
        <div key={label} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Icon size={16} className={accent === 'amber' ? 'text-amber-500' : 'text-slate-400'} />
            {accent === 'amber' && value > 0 && (
              <span className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">Needs setup</span>
            )}
          </div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-400 mt-1">{label}</p>
        </div>
      ))}
    </div>
  );
}


// Publication row

function PublicationRow({ pub, onClick }) {
  const hasSubscriptions = pub.total_subscriptions > 0;

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-slate-200 hover:border-blue-400 rounded-xl p-4 transition-all group shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <HealthDot status={pub.health_status} latestFetch={pub.latest_fetch_at} />
            <h3 className="text-slate-900 font-semibold truncate">{pub.name}</h3>
            {pub.site_type && (
              <span className="text-[10px] uppercase tracking-wider text-slate-500 px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200">
                {pub.site_type}
              </span>
            )}
          </div>
          {pub.domain && (
            <p className="text-xs text-slate-400 ml-5 truncate">{pub.domain}</p>
          )}
        </div>

        <div className="flex items-center gap-6 shrink-0">
          <div className="text-right">
            <p className="text-sm font-medium text-slate-900">{pub.active_subscriptions || 0} / {pub.total_subscriptions || 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Active / Total</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-slate-900">{pub.stories_published || 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Published</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-slate-900">
              {pub.latest_fetch_at ? new Date(pub.latest_fetch_at).toLocaleDateString() : '—'}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Last fetch</p>
          </div>
          {!hasSubscriptions && (
            <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold px-2 py-1 bg-amber-50 border border-amber-200 rounded">
              Add sources
            </span>
          )}
          <ChevronRight size={16} className="text-slate-400 group-hover:text-blue-500" />
        </div>
      </div>
    </button>
  );
}


// Main page

export default function SitePublicationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all'); 

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/feeds/overview');
      setOverview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load publications');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const pubs = overview?.publications || [];
    return pubs.filter(p => {
      if (filter === 'with_subs'    && (p.total_subscriptions || 0) === 0) return false;
      if (filter === 'without_subs' && (p.total_subscriptions || 0) > 0)   return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.name?.toLowerCase().includes(q) && !p.domain?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [overview, search, filter]);

  if (user?.role !== 'admin' && user?.role !== 'editor') {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">You do not have access to Publications.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Site Publications</h1>
        <p className="text-slate-500 text-sm mt-1">All publication sites across the network</p>
      </div>

      <SummaryCards summary={overview?.summary} />

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search publications..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
          {[
            { id: 'all',          label: 'All' },
            { id: 'with_subs',    label: 'Has Sources' },
            { id: 'without_subs', label: 'Needs Setup' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                filter === t.id
                  ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          {search ? 'No publications match your search.' : 'No publications found.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(pub => (
            <PublicationRow
              key={pub.id}
              pub={pub}
              onClick={() => navigate(`/feeds/publications/${pub.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}